import { useRouterState } from "@tanstack/react-router"
import { ChevronRight, ShieldCheck } from "lucide-react"
import { Fragment, type Ref, useEffect, useRef, useState } from "react"

import PayApplePay from "@/assets/img/compatibility/checkout/pay-applepay.svg"
import PayMaestro from "@/assets/img/compatibility/checkout/pay-maestro.svg"
import PayMastercard from "@/assets/img/compatibility/checkout/pay-mastercard.svg"
import PayVisa from "@/assets/img/compatibility/checkout/pay-visa.svg"
import AdvCareer from "@/assets/img/astro-map/adv-career.png"
import VerifiedIcon from "@/assets/img/checkout/verified.svg"
import AdvLines from "@/assets/img/astro-map/adv-lines.png"
import AdvLove from "@/assets/img/astro-map/adv-love.png"
import DiscountBg from "@/assets/img/astro-map/discount-bg.png"
import DiscountStreak from "@/assets/img/astro-map/discount-streak.png"
import IcCareer from "@/assets/img/astro-map/ic-career.svg"
import IcHealth from "@/assets/img/astro-map/ic-health.svg"
import IcRomance from "@/assets/img/astro-map/ic-romance.svg"
import IcSuccess from "@/assets/img/astro-map/ic-success.svg"
import MoonIcon from "@/assets/img/astro-map/moon.svg"
import PlanetJupiter from "@/assets/img/astro-map/sub-planet-jupiter.svg"
import PlanetMoon from "@/assets/img/astro-map/sub-planet-moon.svg"
import PlanetVenus from "@/assets/img/astro-map/sub-planet-venus.svg"
import Review1 from "@/assets/img/astro-map/review-1.png"
import Review2 from "@/assets/img/astro-map/review-2.png"
import Review3 from "@/assets/img/astro-map/review-3.png"
import StarGold from "@/assets/img/astro-map/star-gold.svg"
import SubBgGlow from "@/assets/img/astro-map/sub-bg-glow.png"
import SubImage from "@/assets/img/astro-map/sub-image.png"
import SubLineCareer from "@/assets/img/astro-map/sub-line-career.svg"
import SubLineSuccess from "@/assets/img/astro-map/sub-line-success.svg"
import SubMapHero from "@/assets/img/astro-map/sub-map-hero.png"
import SubMapLock from "@/assets/img/astro-map/sub-map-lock.svg"
import SubOrbit from "@/assets/img/astro-map/sub-orbit.svg"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { hasAdditionalOffers } from "@/features/offers"
import { usePlanSelection } from "@/features/products"
import type { ProductPlan } from "@/features/products/types"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { isValidEmail } from "@/shared/lib/email"
import { formatMoney } from "@/shared/lib/money"
import { AppRoutes } from "@/shared/lib/routes"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { LegalLink } from "@/shared/components/legal-link"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useQuizStore } from "@/stores/quiz-store"
import { buildConsentDisclaimer } from "../domain/disclaimer"
import { savingsPercent, strikePrice } from "../domain/pricing"
import { isApplePayAvailable, useCheckout } from "../hooks/use-checkout"
import { ApplePaySheet } from "./apple-pay-sheet"
import { AstroDiscountBanner } from "./astro-discount-banner"
import { CardPaymentSheet } from "./card-payment-sheet"
import { MoneyBackGuarantee } from "./money-back-guarantee"
import { PaymentButtons } from "./payment-buttons"

type OpenSheet = "card" | "applepay" | null

/** Offer copy shown in the discount sheet (the headline % is marketing, not the charge). */
const PROMO_CODE = "ASTROMAP85"

/** The hero map block reproduces Figma `Astro_Map_Image` (382×300): the globe, a
 * dashed orbit with the moon on it, the four category chips at their frame
 * positions, each with a colour-fading connector line to the globe and a lock
 * badge (a colour disc + dark padlock) — the insights are gated until purchase.
 * Children are placed by their Figma x/y as a % of the 382×300 frame so the whole
 * composition scales with the container. */
const MAP_W = 382
const MAP_H = 300
const MAP_CHIPS = [
  { icon: IcRomance, label: "ROMANCE", color: "#ff537f", x: 8, y: 63, lockX: 111, lockY: 55, line: null, lineBox: null },
  { icon: IcCareer, label: "CAREER", color: "#7fe718", x: 32, y: 151, lockX: 122, lockY: 143, line: SubLineCareer, lineBox: { x: 137.5, y: 124.56, w: 134, h: 32.44 } },
  { icon: IcSuccess, label: "SUCCESS", color: "#ffc014", x: 262, y: 107, lockX: 361, lockY: 97, line: SubLineSuccess, lineBox: { x: 144, y: 130, w: 118.5, h: 95 } },
  { icon: IcHealth, label: "HEALTH", color: "#50e3c2", x: 245, y: 203, lockX: 334, lockY: 195, line: null, lineBox: null },
] as const

/** Place a Figma frame coordinate (px in the 382×300 frame) as a CSS percentage. */
const fx = (px: number) => `${(px / MAP_W) * 100}%`
const fy = (px: number) => `${(px / MAP_H) * 100}%`

/** Locked "planet line" cards in the horizontal slider (Figma `Slider` / `Planet
 * Card`): a gradient icon badge + title + an accent tag chip over a blurred,
 * gated teaser with a light "Unlock to reveal" pill. Accent colour tints the tag. */
const PLANET_LINES = [
  { name: "Moon Line", tags: "HOME • FAMILY • CARE", color: "#50e3c2", icon: PlanetMoon },
  { name: "Venus Line", tags: "LOVE • BEAUTY • HARMONY", color: "#ff537f", icon: PlanetVenus },
  { name: "Jupiter Line", tags: "GROWTH • LUCK • ABUNDANCE", color: "#ffc014", icon: PlanetJupiter },
]

/** What the paywall unlocks (Figma Subscription advantages card). */
const ADVANTAGES = [
  { icon: AdvLove, text: "Find the exact cities where your love life improves." },
  { icon: AdvCareer, text: "See where your career grows fastest based on your chart." },
  {
    icon: AdvLines,
    text: "Discover your strongest planetary lines for love, money, and purpose.",
  },
]

const REVIEWS = [
  {
    avatar: Review1,
    name: "Hannah Brooks",
    text: "I moved to one of my Venus-line cities and within months my whole love life shifted. The map felt eerily accurate.",
  },
  {
    avatar: Review2,
    name: "Ava Thompson",
    text: "Seeing my career lines mapped out gave me the push to relocate. Work opportunities started showing up almost right away.",
  },
  {
    avatar: Review3,
    name: "Ethan Lawson",
    text: "What stood out most was how personal it felt — not generic horoscopes, but actual places tied to my chart and energies.",
  },
]

function ReviewStars() {
  return (
    <div className="flex">
      {Array.from({ length: 5 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed 5-star row
        <img key={i} src={StarGold} alt="" className="size-4" />
      ))}
    </div>
  )
}

/** mm:sec countdown shown in the navbar — two boxed digits with a dotted divider. */
function Countdown({ seconds }: { seconds: number }) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0")
  const ss = String(seconds % 60).padStart(2, "0")
  const box = (value: string, label: string) => (
    <div className="flex h-10 w-12 flex-col items-center justify-center rounded-lg bg-purple-light/10">
      <span className="-mb-1 font-display text-xl font-medium text-purple-light tabular-nums">
        {value}
      </span>
      <span className="text-xs text-purple-light/40">{label}</span>
    </div>
  )
  return (
    <div className="flex items-center gap-1">
      {box(mm, "min")}
      <div className="flex flex-col gap-0.5">
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
      </div>
      {box(ss, "sec")}
    </div>
  )
}

/**
 * "Total due today: ——— $42.99 › $1.00 / You just saved N%" row, from live plan
 * data. Struck = full price (recurring/previous), due-today = the live `price`.
 */
function PriceRow({ plan }: { plan: ProductPlan }) {
  const strike = strikePrice(plan)
  const savings = savingsPercent(plan)
  return (
    <div className="flex items-center gap-4">
      <span className="text-base font-semibold text-purple-light">Total due today:</span>
      <div className="h-px flex-1 bg-purple-light/20" />
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-1">
          {strike != null && (
            <span className="text-base text-purple-light/40 line-through decoration-peach">
              {formatMoney(strike, plan.currency)}
            </span>
          )}
          <ChevronRight className="size-3 text-purple-light/40" />
          <span className="text-base font-semibold text-white">
            {formatMoney(plan.price, plan.currency)}
          </span>
        </div>
        {savings > 0 && (
          <span className="rounded-full bg-green/20 px-2 py-0.5 text-[10px] font-semibold text-green">
            You just saved {savings}%
          </span>
        )}
      </div>
    </div>
  )
}

/** Secure-payment + money-back card shown under each pay block. */
function SecurePaymentCard({ plan }: { plan: ProductPlan }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-3xl border border-white/10 p-4">
      <p className="text-sm font-semibold text-white">Safe &amp; Secure Payment</p>
      <div className="flex w-full gap-1">
        {[
          { src: PayVisa, cls: "h-3 w-[37px]" },
          { src: PayMastercard, cls: "h-6 w-[31px]" },
          { src: PayMaestro, cls: "h-6 w-[31px]" },
          { src: PayApplePay, cls: "h-4 w-[38px]" },
        ].map((p) => (
          <div
            key={p.src}
            className="flex h-10 flex-1 items-center justify-center rounded-lg bg-white/10"
          >
            <img src={p.src} alt="" className={p.cls} />
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-white/10" />
      <MoneyBackGuarantee />
      <p className="text-center text-xs text-purple-light/40">{buildConsentDisclaimer(plan)}</p>
    </div>
  )
}

/**
 * The Astro Map checkout — the `astro-map` checkout variant. Two stages in one
 * route: first a one-time-discount bottom sheet (Figma Quiz Astro Map — Discount),
 * then the full Planetary-Lines subscription paywall (Figma — Subscription) with
 * the real pay buttons. Prices/disclaimers come from the live plan; the headline
 * 85% / promo code are the offer's marketing copy, not the charge. Transactional
 * parts reuse the shared payment primitives; purchase analytics fire inside
 * `useCheckout`.
 */
export function AstroCheckout() {
  const navigate = useNavigateWithParams()
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const purchaseStatus = usePurchaseStore((s) => s.status)
  const email = (useQuizStore((s) => s.answers.email) as string) || ""
  const { selectedPlan, isLoading } = usePlanSelection()

  const [stage, setStage] = useState<"discount" | "paywall">("discount")
  const [sheet, setSheet] = useState<OpenSheet>(null)
  const [applePayAvailable] = useState(isApplePayAvailable)
  const { payWithApplePay } = useCheckout(selectedPlan ?? undefined, email, { initCard: false })

  const paymentRef = useRef<HTMLDivElement>(null)
  const scrollToPayment = () =>
    paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  const [seconds, setSeconds] = useState(9 * 60 + 45)
  const [paymentReached, setPaymentReached] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [])

  // The floating "jump to pay" chrome (top countdown bar + bottom CTA) guides the
  // user down to the price block, then gets out of the way: hide once the block's
  // top scrolls up into the viewport, and stay hidden for everything below it
  // (testimonials, the second price block, footer) so they never cover a real pay
  // button. Re-shows if the user scrolls back up into the teaser.
  useEffect(() => {
    if (stage !== "paywall") return
    const onScroll = () => {
      const el = paymentRef.current
      if (!el) return
      setPaymentReached(el.getBoundingClientRect().top <= window.innerHeight)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [stage])

  // The "85% Discount Activated" banner is a one-shot confirmation: show it on
  // entering the paywall, then fade it out after 4s to reveal the countdown + "Get
  // my results" header sitting underneath it (Figma: banner → timer/CTA header).
  useEffect(() => {
    if (stage !== "paywall") return
    const t = setTimeout(() => setBannerDismissed(true), 4000)
    return () => clearTimeout(t)
  }, [stage])

  // Paywall reached — the subscription sub-screen is shown (analytics brief #33).
  useEffect(() => {
    if (stage === "paywall") trackEvent(ANALYTICS_EVENTS.ASTROMAP_SUB_SCREEN_SHOWN)
  }, [stage])

  const nextRoute = hasAdditionalOffers()
    ? AppRoutes.ADDITIONAL_OFFERS
    : typeof search.upsell === "string" && search.upsell
      ? AppRoutes.UPSELL
      : AppRoutes.CREATE_ACCOUNT

  useEffect(() => {
    if (!selectedPlan) return
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: selectedPlan.id,
      price: selectedPlan.price,
      currency: selectedPlan.currency,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id])

  useEffect(() => {
    if (purchaseStatus === "success") navigate({ to: nextRoute })
  }, [purchaseStatus, navigate, nextRoute])

  const handleSuccess = () => {
    setSheet(null)
    navigate({ to: nextRoute })
  }

  const handleApplePay = async () => {
    trackEvent(ANALYTICS_EVENTS.ASTROMAP_SUB_SCREEN_SUB_BUTTON_CLICK, {
      metadata: { method: "apple" },
    })
    if (isValidEmail(email)) {
      const ok = await payWithApplePay()
      if (ok) handleSuccess()
    } else {
      setSheet("applepay")
    }
  }

  if (isLoading || !selectedPlan) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-dark-base">
        <LoadingSpinner />
      </div>
    )
  }

  const renderPay = (ref?: Ref<HTMLDivElement>) => (
    <div className="space-y-4">
      <PriceRow plan={selectedPlan} />
      <div ref={ref}>
        <PaymentButtons
          applePayAvailable={applePayAvailable}
          onApplePay={handleApplePay}
          onCard={() => {
            trackEvent(ANALYTICS_EVENTS.ASTROMAP_SUB_SCREEN_SUB_BUTTON_CLICK, {
              metadata: { method: "card" },
            })
            setSheet("card")
          }}
        />
      </div>
    </div>
  )

  // ── Stage 1: one-time discount bottom sheet ────────────────────────────────
  if (stage === "discount") {
    return (
      <div className="relative min-h-[100dvh] overflow-hidden bg-dark-base font-body text-purple-light">
        {/* Keep the blurred backdrop + sheet inside the mobile app column so the
            paywall preview doesn't stretch full-width on desktop; the paywall
            stage is column-constrained too, so this makes the two stages match. */}
        <div className="relative mx-auto min-h-[100dvh] w-full max-w-app overflow-hidden">
          <img
            src={DiscountBg}
            alt=""
            className="astro-sheet-fade pointer-events-none absolute inset-0 size-full object-cover object-top blur-[10px]"
          />
          <div className="astro-sheet-fade absolute inset-0 bg-dark-base/80" />
          <MobileLayout className="relative flex min-h-[100dvh] flex-col justify-end">
            <div className="astro-sheet-up overflow-hidden rounded-t-[24px] bg-dark-surface pb-4">
            <img src={DiscountStreak} alt="" className="-mb-4 w-full object-cover" />
            <div className="flex flex-col gap-4 px-4">
              <h1 className="text-center font-display text-2xl font-medium text-purple-light">
                You get an exclusive one-time promo code for a{" "}
                <span className="bg-gradient-to-br from-peach to-purple-accent bg-clip-text text-transparent">
                  85% discount
                </span>
              </h1>
              <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-main-purple bg-main-purple/10 px-4 py-4">
                <span className="text-xl font-semibold text-white">{PROMO_CODE}</span>
              </div>
              <Button
                variant="gradient"
                size="xl"
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.ASTROMAP_PROMOCODE_CLICK)
                  setStage("paywall")
                }}
              >
                Continue
              </Button>
            </div>
          </div>
          </MobileLayout>
        </div>
      </div>
    )
  }

  // ── Stage 2: the Planetary-Lines subscription paywall ──────────────────────
  return (
    <div className="min-h-[100dvh] bg-dark-base font-body text-purple-light">
      <AstroDiscountBanner hidden={paymentReached || bannerDismissed} />
      {/* Countdown + "Get my results" — pinned to the top for the whole paywall,
          never hidden on scroll. */}
      <div className="sticky top-0 z-20 border-purple-light/10 border-b bg-dark-base">
        <MobileLayout className="flex items-center justify-between py-2">
          <Countdown seconds={seconds} />
          <button
            type="button"
            onClick={scrollToPayment}
            className="flex h-10 w-[180px] items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            Get my results
          </button>
        </MobileLayout>
      </div>

      {/* Sticky bottom CTA — floats over the scrolling teaser (the slider/cards
          pass behind it) and slides away once the real price block is on screen,
          the same trigger as the top "Get my results" bar. */}
      <div
        className={cn(
          "pointer-events-none fixed inset-x-0 bottom-0 z-30 transition-all duration-200",
          paymentReached && "translate-y-full opacity-0",
        )}
      >
        <div className="mx-auto w-full max-w-app">
          {/* Short fade so the cards above dissolve into the bar instead of a hard edge. */}
          <div className="h-10 bg-gradient-to-t from-dark-base to-transparent" />
          {/* Solid bar — the button and the notice sit on opaque dark-base, never see-through. */}
          <div className="bg-dark-base px-4 pb-4">
            <button
              type="button"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.ASTROMAP_SUB_SCREEN_BUTTON_CLICK)
                scrollToPayment()
              }}
              className="pointer-events-auto h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
            >
              Show me where I belong
            </button>
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-purple-light/40">
              <ShieldCheck className="size-4" />
              We do not collect your data
            </div>
          </div>
        </div>
      </div>

      <MobileLayout className="relative isolate flex flex-col pt-10 pb-6">
        {/* Hero */}
        <img
          src={SubBgGlow}
          alt=""
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[240px] w-full object-cover"
        />
        <div className="mx-auto w-fit rounded-full bg-green/20 px-3 py-2 text-center text-[10px] font-medium uppercase tracking-wide text-green backdrop-blur">
          Your Astro Map is ready
        </div>
        <h1 className="mt-5 text-center font-display text-2xl font-medium text-purple-light">
          Discover your <span className="text-main-purple">Planetary Lines!</span>
        </h1>
        <p className="mt-2 text-center text-base text-purple-light/70">
          These are the key energies that shape your ideal locations.
        </p>

        {/* Astro-map block — Figma `Astro_Map_Image`: the globe, a dashed orbit with
            the moon on it, colour-fading connector lines and the four locked chips. */}
        <div className="relative mx-auto mt-6 aspect-[382/300] w-full max-w-[382px] overflow-hidden rounded-[24px]">
          <img src={SubMapHero} alt="" className="absolute inset-0 size-full object-cover" />

          {/* Dashed orbit (Ellipse 239) — Ø256 centred in the frame. */}
          <img
            src={SubOrbit}
            alt=""
            className="pointer-events-none absolute"
            style={{ left: fx(63), top: fy(25), width: fx(256), height: fy(256) }}
          />

          {/* Connector lines fading from each chip toward the globe. */}
          {MAP_CHIPS.map((chip) =>
            chip.line && chip.lineBox ? (
              <img
                key={`line-${chip.label}`}
                src={chip.line}
                alt=""
                className="pointer-events-none absolute"
                style={{
                  left: fx(chip.lineBox.x),
                  top: fy(chip.lineBox.y),
                  width: fx(chip.lineBox.w),
                  height: fy(chip.lineBox.h),
                }}
              />
            ) : null,
          )}

          {/* Moon — sits on the orbit, top-right. */}
          <img
            src={MoonIcon}
            alt=""
            className="absolute"
            style={{ left: fx(247), top: fy(31), width: fx(34), height: fy(34) }}
          />

          {/* Category chips + their lock badges (colour disc + dark padlock). */}
          {MAP_CHIPS.map((chip) => (
            <Fragment key={chip.label}>
              <div
                className="absolute flex items-center gap-2 rounded-full py-2 pl-3 pr-4 backdrop-blur-[6px]"
                style={{
                  left: fx(chip.x),
                  top: fy(chip.y),
                  background: `linear-gradient(0deg, ${chip.color}33, ${chip.color}33), linear-gradient(0deg, rgba(13, 8, 28, 0.4), rgba(13, 8, 28, 0.4))`,
                }}
              >
                <img src={chip.icon} alt="" className="size-5" />
                <span className="text-xs font-semibold uppercase" style={{ color: chip.color }}>
                  {chip.label}
                </span>
              </div>
              <div
                className="absolute flex items-center justify-center rounded-full"
                style={{
                  left: fx(chip.lockX + 3),
                  top: fy(chip.lockY + 3),
                  width: fx(18),
                  height: fy(18),
                  background: chip.color,
                  border: "2px solid #0d081c",
                }}
              >
                <img src={SubMapLock} alt="" className="h-[10px] w-[9px]" />
              </div>
            </Fragment>
          ))}
        </div>

        {/* Planet-line slider (Figma `Slider`) — locked teaser cards. */}
        <div className="no-scrollbar -mr-4 mt-4 flex gap-4 overflow-x-auto pr-4">
          {PLANET_LINES.map((line) => (
            <div
              key={line.name}
              className="flex w-[300px] shrink-0 flex-col gap-4 rounded-[24px] p-4"
              style={{
                background: "linear-gradient(180deg, rgba(223, 203, 245, 0.1), rgba(223, 203, 245, 0))",
              }}
            >
              {/* Gradient icon badge with the planet glyph. */}
              <div
                className="flex size-14 items-center justify-center rounded-full"
                style={{
                  background: "linear-gradient(180deg, rgba(223, 203, 245, 0.4), rgba(149, 101, 253, 0.2))",
                }}
              >
                <img src={line.icon} alt="" className="size-9" />
              </div>

              {/* Title + accent tag chip. */}
              <div className="flex flex-col gap-2">
                <p className="font-display text-xl font-medium text-purple-light">{line.name}</p>
                <span
                  className="w-fit rounded-[14px] px-3 py-1.5 text-xs font-medium uppercase"
                  style={{ backgroundColor: `${line.color}1a`, color: line.color }}
                >
                  {line.tags}
                </span>
              </div>

              {/* Locked teaser: blurred, downward-fading text under a light unlock pill. */}
              <div className="relative h-[92px] overflow-hidden">
                <p
                  className="bg-clip-text text-base font-light leading-5 text-transparent blur-[6px]"
                  style={{
                    backgroundImage:
                      "linear-gradient(178deg, rgba(223, 203, 245, 0.7) 5%, rgba(223, 203, 245, 0) 95%)",
                  }}
                >
                  The cities where this energy peaks — and the months it quietly works in your
                  favour — are written along this line.
                </p>
                <div className="absolute left-1/2 top-1/2 flex h-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-2 rounded-[28px] bg-purple-light pl-3 pr-4">
                  <span className="flex size-5 shrink-0 items-center justify-center">
                    <img src={SubMapLock} alt="" className="h-[17px] w-[15px]" />
                  </span>
                  <span className="whitespace-nowrap text-sm font-semibold text-dark-base">
                    Unlock to reveal
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* What you unlock */}
        <h2 className="mt-10 text-center font-display text-2xl font-medium text-purple-light">
          See what you can uncover and enjoy inside
        </h2>
        <div className="mt-4 flex flex-col gap-2 rounded-[20px] bg-dark-surface p-4">
          {ADVANTAGES.map((a, i) => (
            <Fragment key={a.text}>
              {i > 0 && <div className="h-px w-full bg-purple-light/10" />}
              <div className="flex items-center gap-2.5">
                <div className="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-[26px] bg-dark-base">
                  <img src={a.icon} alt="" className="size-10 object-contain" />
                </div>
                <p className="min-w-0 text-base text-white">{a.text}</p>
              </div>
            </Fragment>
          ))}
        </div>

        {/* Primary price block */}
        <div className="mt-8">{renderPay(paymentRef)}</div>
        <div className="mt-4">
          <SecurePaymentCard plan={selectedPlan} />
        </div>

        {/* Testimonials */}
        <div className="-mx-4 mt-10 flex flex-col gap-6 bg-dark-surface px-4 py-10">
          <h2 className="font-display text-[32px] font-medium leading-tight text-purple-light">
            What people uncovered through their Astro Map
          </h2>
          <div className="flex items-center gap-2">
            <img src={StarGold} alt="" className="size-8" />
            <span className="font-display text-2xl font-medium">
              <span className="text-gold">4.8</span>
              <span className="text-purple-light"> (2545 readings)</span>
            </span>
          </div>
          <div className="no-scrollbar -mr-4 flex gap-4 overflow-x-auto pr-4 pb-2">
            {REVIEWS.map((r) => (
              <div
                key={r.name}
                className="flex w-[300px] shrink-0 flex-col justify-between gap-6 rounded-2xl border border-purple-light/20 p-4"
              >
                <p className="text-base text-purple-light/70">{r.text}</p>
                <div className="flex items-center gap-2">
                  <img src={r.avatar} alt="" className="size-11 rounded-full object-cover" />
                  <div className="flex flex-col gap-1">
                    <span className="font-display text-base font-medium text-purple-light">
                      {r.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <ReviewStars />
                      <span className="text-sm font-medium text-gold">4.8</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm">
            <span className="font-medium text-white">*Disclaimer: </span>
            <span className="text-purple-light/70">
              Every Astro Map is unique and based on your personal birth details and the planetary
              lines derived from your chart.
            </span>
          </p>
        </div>

        {/* Second offer */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <img
            src={SubImage}
            alt=""
            className="-mx-4 -mb-10 w-[calc(100%+2rem)] max-w-none object-cover"
          />
          <div className="space-y-2 text-center">
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Decode the map that's written in the stars
            </h2>
            <p className="text-base text-purple-light/70">
              Unlock your planetary lines for love, career, and purpose — and the cities where they
              shine brightest.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-4 rounded-3xl bg-dark-surface p-5">
          {renderPay()}
          <p className="text-center text-xs text-purple-light/40">
            {buildConsentDisclaimer(selectedPlan)}
          </p>
        </div>

        {/* Satisfaction guarantee */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <ShieldCheck className="size-20 text-main-purple" />
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Satisfaction Guaranteed
            </h2>
            <p className="text-base text-purple-light/70">
              At Live Palmistry, every Astro Map and planetary-line reading is designed to feel
              personal, meaningful, and worth exploring. If your experience doesn't feel insightful,
              we're here for you.
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 rounded-[20px] bg-purple-light/10 px-4 pt-2 pb-3">
            <div className="text-center">
              <p className="font-display text-2xl font-medium text-purple-light">4.8 out of 5</p>
              <p className="text-sm text-purple-light/70">Astro Maps revealed worldwide</p>
            </div>
            <div className="h-px w-full bg-purple-light/10" />
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <ReviewStars />
                  <span className="text-sm font-medium text-gold">4.8</span>
                </div>
                <p className="text-xs text-purple-light/40">Based on user experiences</p>
              </div>
              <div className="flex items-center gap-1 rounded-2xl bg-green/10 py-1.5 pr-2 pl-1.5">
                <img src={VerifiedIcon} alt="" className="size-4" />
                <span className="text-xs font-semibold text-green">Verified reviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-10 flex flex-col gap-2 text-xs text-purple-light/40">
          <p className="text-center">All rights reserved. 2026</p>
          <div className="h-px w-full bg-purple-light/10" />
          <div className="flex text-center">
            <LegalLink to={AppRoutes.PRIVACY} className="flex-1 hover:text-purple-light">
              Privacy Policy
            </LegalLink>
            <LegalLink to={AppRoutes.TERMS} className="flex-1 hover:text-purple-light">
              Terms of use
            </LegalLink>
            <LegalLink to={AppRoutes.COOKIES} className="flex-1 hover:text-purple-light">
              Cookie Policy
            </LegalLink>
          </div>
        </footer>
      </MobileLayout>

      <CardPaymentSheet
        plan={selectedPlan}
        email={email}
        open={sheet === "card"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
      <ApplePaySheet
        plan={selectedPlan}
        email={email}
        open={sheet === "applepay"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
    </div>
  )
}
